import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PlusCircle, Pencil, Trash2, Users, Award, Ticket } from "lucide-react";
import { MaskedEmail } from "@/components/MaskedEmail";
import { WinnerReveal } from "@/components/WinnerReveal";

export default function AdminDashboard() {
  const [comps, setComps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reveal, setReveal] = useState(null); // {winner, ticketPool, title}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/competitions");
      setComps(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!confirm("Delete this raffle? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/competitions/${id}`);
      toast.success("Raffle deleted");
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === "string" && detail.startsWith("HAS_ENTRIES:")) {
        const sold = detail.split(":")[1];
        if (!confirm(`This raffle has ${sold} sold ticket(s). Permanently delete the raffle AND all its entries? Buyers will lose their tickets. This cannot be undone.`)) return;
        try {
          await api.delete(`/admin/competitions/${id}?force=true`);
          toast.success("Raffle and all its entries deleted");
          load();
        } catch (e2) {
          toast.error(formatApiError(e2.response?.data?.detail));
        }
        return;
      }
      toast.error(formatApiError(detail));
    }
  };

  const handleDraw = async (id) => {
    if (!confirm("Run the winner draw now? The live reveal wheel will play. This is final.")) return;
    try {
      let pool = [];
      try {
        const { data: entries } = await api.get(`/admin/competitions/${id}/entries`);
        pool = entries.map((e) => e.ticket_number);
      } catch {
        /* wheel falls back to generic numbers */
      }
      const { data } = await api.post(`/admin/competitions/${id}/draw`);
      if (data.already_drawn) {
        toast.info(`Already drawn — winner: ${data.winner?.user_name || "—"} (ticket #${data.winner?.ticket_number})`);
        return;
      }
      const comp = comps.find((c) => c.id === id);
      setReveal({ winner: data.winner, ticketPool: pool, title: comp?.title || "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const closeReveal = () => {
    setReveal(null);
    load();
  };

  return (
    <div data-testid="admin-dashboard">
      {reveal && (
        <WinnerReveal
          open
          compTitle={reveal.title}
          winner={reveal.winner}
          ticketPool={reveal.ticketPool}
          onClose={closeReveal}
        />
      )}
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--ufr-green)] font-bold">Raffles</div>
          <h1 className="font-display text-4xl md:text-5xl text-white mt-2">All raffles</h1>
        </div>
        <Link to="/admin/new" className="btn-ufr-primary px-5 py-3 text-sm inline-flex items-center gap-2" data-testid="admin-new-raffle-btn">
          <PlusCircle size={16} /> New raffle
        </Link>
      </div>

      {loading ? (
        <div className="text-white/50">Loading…</div>
      ) : comps.length === 0 ? (
        <div className="ufr-card rounded-md p-10 text-center">
          <div className="font-display text-2xl text-white">No raffles yet</div>
          <p className="text-white/60 text-sm mt-2">Create your first football card raffle.</p>
          <Link to="/admin/new" className="btn-ufr-primary inline-block px-6 py-3 text-sm mt-5">Create raffle</Link>
        </div>
      ) : (
        <div className="space-y-3" data-testid="admin-raffles-list">
          {comps.map((c) => (
            <div key={c.id} className="ufr-card rounded-md p-4 grid grid-cols-12 gap-4 items-center" data-testid={`admin-raffle-row-${c.id}`}>
              <div className="col-span-12 sm:col-span-1">
                <div className="w-16 h-20 bg-black rounded-sm overflow-hidden border border-white/10">
                  {c.image_url && <img src={c.image_url} alt="" className="w-full h-full object-cover" />}
                </div>
              </div>
              <div className="col-span-12 sm:col-span-5">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--ufr-green)] font-bold">{c.subtitle}</div>
                <div className="font-display text-lg text-white">{c.title}</div>
                <div className="text-xs text-white/50 mt-1 flex items-center gap-3">
                  <span><Ticket size={11} className="inline mr-1" />{c.tickets_sold || 0} / {c.total_tickets}</span>
                  <span>£{c.price_per_ticket}/ticket</span>
                  <span className={`uppercase font-bold ${c.status === "ended" ? "text-yellow-300" : "text-[color:var(--ufr-green)]"}`}>{c.status}</span>
                </div>
              </div>
              <div className="col-span-12 sm:col-span-3 text-xs text-white/60">
                {c.winner ? (
                  <div className="text-yellow-300 font-bold flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="flex items-center gap-1"><Award size={12} /> Won by {c.winner.user_name || "—"} (#{c.winner.ticket_number})</span>
                    <MaskedEmail email={c.winner.user_email} className="text-yellow-300/70 !text-[11px] font-normal" testid={`admin-winner-email-${c.id}`} />
                  </div>
                ) : (
                  <span>Ends {new Date(c.ends_at).toLocaleString()}</span>
                )}
              </div>
              <div className="col-span-12 sm:col-span-3 flex flex-wrap items-center justify-end gap-2">
                <Link to={`/admin/${c.id}/entries`} className="btn-ufr-ghost px-3 py-2 text-[11px] flex items-center gap-1" data-testid={`admin-entries-btn-${c.id}`}>
                  <Users size={12} /> Entries
                </Link>
                {!c.winner && (
                  <button onClick={() => handleDraw(c.id)} className="btn-ufr-primary px-3 py-2 text-[11px] flex items-center gap-1" data-testid={`admin-draw-btn-${c.id}`}>
                    <Award size={12} /> Draw
                  </button>
                )}
                <Link to={`/admin/${c.id}/edit`} className="btn-ufr-ghost px-3 py-2 text-[11px]" data-testid={`admin-edit-btn-${c.id}`}>
                  <Pencil size={12} />
                </Link>
                <button onClick={() => handleDelete(c.id)} className="btn-ufr-ghost px-3 py-2 text-[11px] hover:!border-red-500 hover:!text-red-400" data-testid={`admin-delete-btn-${c.id}`}>
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
